import React, { useState, useEffect } from "react";
import "../assets/styles/standardTree.css";
import {
  getAllMainStandardsAdminAsync,
  getSubStandardsByMainAsync,
  getItemsBySubStandardAsync,
} from "../services/standarsdService";
import { getDepositForm } from "../services/depositService";
import { useNavigate } from 'react-router-dom';
import { routePath } from "../routes/routePath";
import LoadingSpinnerComponent from "./LoadingSpinnerComponent";
import OverlayLoadingSpinner from "./OverlayLoadingSpinner";
import errorMessages from "../constants/errorMessages";

const StandardTree = () => {
  const [mainStandards, setMainStandards] = useState([]);
  const [subStandardsMap, setSubStandardsMap] = useState({});
  const [itemsMap, setItemsMap] = useState({});

  const [openMainStandards, setOpenMainStandards] = useState([]);
  const [openSubStandards, setOpenSubStandards] = useState([]);

  const [loading, setLoading] = useState(true);
  const [itemClickLoading, setItemClickLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // ─── Page load: fetch MainStandards only ─────────────────────────────────
  useEffect(() => {
    const fetchMainStandards = async () => {
      try {
        const res = await getAllMainStandardsAdminAsync();
        const sorted = (res.result || []).sort((a, b) => a.sequence - b.sequence);
        setMainStandards(sorted);
      } catch (err) {
        setError(errorMessages.FETCH_DATA_ERROR);
      } finally {
        setLoading(false);
      }
    };

    fetchMainStandards();
  }, []);

  // ─── MainStandard click: toggle + fetch SubStandards ─────────────────────
  const toggleMainStandard = async (id) => {
    setOpenMainStandards((prev) =>
      prev.includes(id) ? prev.filter((mId) => mId !== id) : [...prev, id]
    );

    // Already cached — skip fetch
    if (subStandardsMap[id] !== undefined) return;

    try {
      const res = await getSubStandardsByMainAsync(id);
      const sorted = (res.result || []).sort((a, b) => a.sequence - b.sequence);
      setSubStandardsMap((prev) => ({ ...prev, [id]: sorted }));
    } catch (err) {
      // 404 = no SubStandards yet, treat as empty
      setSubStandardsMap((prev) => ({ ...prev, [id]: [] }));
    }
  };

  // ─── SubStandard click: toggle + fetch Items ──────────────────────────────
  const toggleSubStandard = async (id) => {
    setOpenSubStandards((prev) =>
      prev.includes(id) ? prev.filter((sId) => sId !== id) : [...prev, id]
    );

    // Already cached — skip fetch
    if (itemsMap[id] !== undefined) return;

    try {
      const res = await getItemsBySubStandardAsync(id);
      const sorted = (res.result || [])
        .sort((a, b) => a.sequence - b.sequence)
        .map((item) => ({
          id: item.itemId,
          name: item.name,
          validationGroupVersionId: item.validationGroupVersionId,
          isActive: item.isActive,
          score:item.score
        }));
      setItemsMap((prev) => ({ ...prev, [id]: sorted }));
    } catch (err) {
      // 404 = no Items yet, treat as empty
      setItemsMap((prev) => ({ ...prev, [id]: [] }));
    }
  };

  // ─── Item click: unchanged ────────────────────────────────────────────────
  const openNewDepositForm = async (item) => {
    const spinnerDelay = 500;
    let spinnerTimeout;

    try {
      spinnerTimeout = setTimeout(() => setItemClickLoading(true), spinnerDelay);

      const response = await getDepositForm(item.id);
      navigate(routePath.newDeposit(item.id), {
        state: {
          itemVersionId: item.itemVersionId,
          validationGroupVersionId: item.validationGroupVersionId,
          validationFields: response.validationFields,
          depositForm: response.depositForm,
        },
      });
    } finally {
      clearTimeout(spinnerTimeout);
      setItemClickLoading(false);
    }
  };

  if (loading) return <LoadingSpinnerComponent />;
  if (error) return <div>{error}</div>;

  return (
    <div className="standard-tree">
      {mainStandards.map((standard) => {
        const subStandards = subStandardsMap[standard.mainStandardId] || [];
        return (
          <div key={standard.mainStandardId} className="mb-4">
            <div
              className="main-standard primary-gradient"
              onClick={() => toggleMainStandard(standard.mainStandardId)}
            >
              {standard.sequence}
              <span> </span>
              {standard.name}
            </div>
            <div
              className={`sub-standards ${
                openMainStandards.includes(standard.mainStandardId) ? "expanded" : ""
              }`}
            >
              {subStandards.map((subStandard) => {
                const items = itemsMap[subStandard.subStandardId] || [];
                return (
                  <div key={subStandard.subStandardId}>
                    <div
                      className="sub-standard"
                      onClick={() => toggleSubStandard(subStandard.subStandardId)}
                    >
                      {standard.sequence}
                      <span> / </span>
                      {subStandard.sequence}
                      <span> </span>
                      {subStandard.name}
                    </div>
                    <div
                      className={`items ${
                        openSubStandards.includes(subStandard.subStandardId) ? "expanded" : ""
                      }`}
                    >
                      {items.map((item, index) => (
                        <div
                          key={item.id}
                          className="item"
                          onClick={() => openNewDepositForm(item)}
                        >
                          {standard.sequence}
                          <span> / </span>
                          {subStandard.sequence}
                          <span> - </span>
                          {index + 1}
                          <span> {item.name}</span>
                          <span className="badge secondary-bg mx-2"> النقاط : <span> {item.score}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {itemClickLoading && <OverlayLoadingSpinner />}
    </div>
  );
};

export default StandardTree;